import { onError } from '@apollo/client/link/error';
import { FetchResult, Observable } from '@apollo/client';
import {
    logRequestWithoutResponse,
    logUnexpectedError,
    refreshAccessToken,
    shouldRetryRequestWithoutResponse,
} from '../api';
import { sendApolloResponseAsLog } from '@upheal/utils/logger';
import { isGraphQLUnauthorized, isHttpUnauthorized } from './isAuthError';
import { isQuery } from './operation-utils';

const HANDLED_SEARCH_ERROR_TYPES = new Set([
    'SEARCH_TIMEOUT',
    'SEARCH_TEMPORARILY_UNAVAILABLE',
]);

export const errorLink = onError((errorResponse) => {
    const { networkError, graphQLErrors, operation, forward } = errorResponse;
    const context = operation.getContext();
    const requestDuration = performance.now() - context.startTime;
    const operationIsQuery = isQuery(operation);

    if (networkError) {
        sendApolloResponseAsLog(operation);
    }

    const isUnauthorized =
        isHttpUnauthorized(networkError) ||
        isGraphQLUnauthorized(graphQLErrors);

    if (isUnauthorized) {
        const observable = new Observable<FetchResult<Record<string, any>>>(
            (observer) => {
                refreshAccessToken()
                    .then(() => {
                        const subscriber = {
                            next: observer.next.bind(observer),
                            error: observer.error.bind(observer),
                            complete: observer.complete.bind(observer),
                        };

                        forward(operation).subscribe(subscriber);
                    })
                    .catch((err) => {
                        observer.error(err);
                    });
            },
        );

        return observable;
    }

    // @ts-ignore
    if (networkError && !networkError.statusCode) {
        const logError = () => {
            logRequestWithoutResponse({
                errorTitle: `GraphQL ${operation.operationName}`,
                correlationId: context.headers?.['correlation-id'],
                requestHeaders: context.headers,
                extra: {
                    requestDuration,
                    networkError,
                },
            });
        };

        if (
            shouldRetryRequestWithoutResponse({
                alreadyRetried: operation.operationName === 'Retry',
                isIdempotent: operationIsQuery,
                requestDuration,
            })
        ) {
            const observable = forward(operation);
            observable.subscribe({
                error: logError,
            });
        } else {
            logError();
        }
    }

    const unexpectedErrors = graphQLErrors?.filter(({ extensions }) => {
        return (
            extensions?.classification === 'INTERNAL_ERROR' &&
            !HANDLED_SEARCH_ERROR_TYPES.has(extensions?.errorType as string)
        );
    });

    if (unexpectedErrors && unexpectedErrors.length > 0) {
        logUnexpectedError({
            errorTitle: `GraphQL ${operation.operationName}`,
            correlationId: context.headers?.['correlation-id'],
            requestHeaders: context.headers,
            responseHeaders: context.response?.headers,
            extra: {
                errors: unexpectedErrors,
                requestDuration,
            },
        });
    }
});
